/**
 * Upgrade Mining Dialog
 * Confirms upgrade of mining facility on an asteroid
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager } from '../../managers/MMONetworkManager';
import { MINING_FACILITY_COSTS, MINING_PRODUCTION } from '../../shared/constants/UnitStats';
import {
  createDialogChrome,
  Colors, CSSColors, Fonts, FontSize, Space,
} from './GameUITheme';
import { isMobile } from '../../utils/ResponsiveHelper';

export class UpgradeMiningDialog extends Phaser.GameObjects.Container {
  private dw: number;
  private mmoState: MMOStateManager;
  private mmoNetwork: MMONetworkManager;
  private asteroidId: string;
  private currentLevel: number;

  constructor(
    scene: Phaser.Scene,
    mmoState: MMOStateManager,
    mmoNetwork: MMONetworkManager,
    asteroidId: string,
    asteroidName: string,
    currentLevel: number,
  ) {
    const mobile = isMobile(scene);
    const dialogW = mobile ? 340 : 652;
    const dh = mobile ? 480 : 400;
    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - dialogW / 2, height / 2 - dh / 2);
    this.dw = dialogW;

    this.mmoState = mmoState;
    this.mmoNetwork = mmoNetwork;
    this.asteroidId = asteroidId;
    this.currentLevel = currentLevel;

    // Standard chrome
    createDialogChrome(scene, this, this.dw, dh, {
      title: `UPGRADE MINING — ${asteroidName}`,
      onClose: () => this.close(),
    });

    // Build content
    this.buildContent();

    this.setDepth(2000);
    scene.add.existing(this);
  }

  private buildContent(): void {
    let y = 64;

    if (this.currentLevel >= 3) {
      this.showMaxLevel(y);
      return;
    }

    const nextLevel = this.currentLevel + 1;
    const upgradeCost = this.getUpgradeCost(nextLevel);
    const currentProduction = this.getProduction(this.currentLevel);
    const nextProduction = this.getProduction(nextLevel);
    const credits = this.mmoState.getResources();
    const canAfford = credits >= upgradeCost;
    const cx = this.dw / 2;

    // Current level
    const currentText = this.scene.add.text(cx, y, `Current Level: ${this.currentLevel}`, {
      fontSize: FontSize.lg,
      color: '#ffffff',
      fontFamily: Fonts.heading,
    });
    currentText.setOrigin(0.5, 0);
    this.add(currentText);
    y += 28;

    const currentProdText = this.scene.add.text(cx, y, `Production: ${currentProduction} ISD/turn`, {
      fontSize: FontSize.sm,
      color: CSSColors.textDim,
      fontFamily: Fonts.body,
    });
    currentProdText.setOrigin(0.5, 0);
    this.add(currentProdText);
    y += 32;

    // Divider
    const divider = this.scene.add.graphics();
    divider.lineStyle(1, Colors.cyan, 0.12);
    divider.lineBetween(Space.lg, y, this.dw - Space.lg, y);
    this.add(divider);
    y += 20;

    // Upgrade target
    const upgradeText = this.scene.add.text(cx, y, `Upgrade to Level ${nextLevel}`, {
      fontSize: FontSize.lg,
      color: CSSColors.green,
      fontFamily: Fonts.heading,
    });
    upgradeText.setOrigin(0.5, 0);
    this.add(upgradeText);
    y += 28;

    const nextProdText = this.scene.add.text(cx, y, `Production: ${nextProduction} ISD/turn (+${nextProduction - currentProduction})`, {
      fontSize: FontSize.sm,
      color: CSSColors.textDim,
      fontFamily: Fonts.body,
    });
    nextProdText.setOrigin(0.5, 0);
    this.add(nextProdText);
    y += 32;

    // Cost
    const costColor = canAfford ? CSSColors.gold : '#ff4444';
    const costText = this.scene.add.text(cx, y, `Cost: ${upgradeCost.toLocaleString()} ISD`, {
      fontSize: FontSize.lg,
      color: costColor,
      fontFamily: Fonts.heading,
    });
    costText.setOrigin(0.5, 0);
    this.add(costText);
    y += 28;

    // Your credits
    const creditsText = this.scene.add.text(cx, y, `Your ISD: ${credits.toLocaleString()}`, {
      fontSize: FontSize.sm,
      color: CSSColors.textDim,
      fontFamily: Fonts.body,
    });
    creditsText.setOrigin(0.5, 0);
    this.add(creditsText);
    y += 36;

    if (!canAfford) {
      const cannotText = this.scene.add.text(cx, y, 'Insufficient ISD', {
        fontSize: FontSize.sm,
        color: '#ff4444',
        fontFamily: Fonts.body,
      });
      cannotText.setOrigin(0.5, 0);
      this.add(cannotText);
      y += 28;
    }

    // Action buttons
    this.createActionButtons(y, canAfford);
  }

  private showMaxLevel(y: number): void {
    const cx = this.dw / 2;

    const maxText = this.scene.add.text(cx, y + 40, 'Mining Facility at Maximum Level', {
      fontSize: FontSize.lg,
      color: CSSColors.green,
      fontFamily: Fonts.heading,
    });
    maxText.setOrigin(0.5, 0);
    this.add(maxText);

    const prodText = this.scene.add.text(cx, y + 72, `Production: ${this.getProduction(this.currentLevel)} ISD/turn`, {
      fontSize: FontSize.sm,
      color: CSSColors.textDim,
      fontFamily: Fonts.body,
    });
    prodText.setOrigin(0.5, 0);
    this.add(prodText);

    const infoText = this.scene.add.text(cx, y + 100, 'No further upgrades available.', {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    infoText.setOrigin(0.5, 0);
    this.add(infoText);
  }

  private createActionButtons(y: number, canAfford: boolean): void {
    const btnW = 180;
    const btnH = 38;

    // Cancel
    this.createBtn(this.dw / 2 - btnW - 10, y, btnW, btnH, 'Cancel', 0x660000, () => this.close());

    // Confirm
    if (canAfford) {
      this.createBtn(this.dw / 2 + 10, y, btnW, btnH, 'Upgrade', 0x006600, () => this.confirmUpgrade());
    }
  }

  private createBtn(x: number, y: number, w: number, h: number, label: string, color: number, onClick: () => void): void {
    const bg = this.scene.add.rectangle(x, y, w, h, color, 0.8);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, color + 0x222222, 0.6);
    bg.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    this.add(bg);

    const text = this.scene.add.text(x + w / 2, y + h / 2, label, {
      fontSize: FontSize.md,
      color: '#ffffff',
      fontFamily: Fonts.heading,
    });
    text.setOrigin(0.5);
    this.add(text);

    bg.on('pointerover', () => {
      bg.setFillStyle(color + 0x222222, 1);
      this.scene.input.setDefaultCursor('pointer');
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(color, 0.8);
      this.scene.input.setDefaultCursor('default');
    });
    bg.on('pointerdown', onClick);
  }

  private getUpgradeCost(level: number): number {
    const costs: Record<number, number> = {
      1: MINING_FACILITY_COSTS.level1,
      2: MINING_FACILITY_COSTS.level2,
      3: MINING_FACILITY_COSTS.level3,
    };
    return costs[level] || 0;
  }

  private getProduction(level: number): number {
    const production: Record<number, number> = {
      0: 0,
      1: MINING_PRODUCTION.level1,
      2: MINING_PRODUCTION.level2,
      3: MINING_PRODUCTION.level3,
    };
    return production[level] || 0;
  }

  private confirmUpgrade(): void {
    console.log(`[UpgradeMining] Upgrading mining on asteroid ${this.asteroidId}`);
    this.mmoNetwork.sendUpgradeMining(this.asteroidId);
    this.close();
  }

  private close(): void {
    this.scene.input.setDefaultCursor('default');
    this.destroy();
  }
}
